import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import { ReactComponent as MinusDisabledBtn } from '../../assets/btn-coin-minus-disabled.svg';
import { ReactComponent as MinusPressedBtn } from '../../assets/btn-coin-minus-pressed.svg';
import { ReactComponent as PlusDisabledBtn } from '../../assets/btn-coin-plus-disabled.svg';
import { ReactComponent as PlusPressedBtn } from '../../assets/btn-coin-plus-pressed.svg';
import useIsMobile from '../../hooks/useIsMobile';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { modalState } from '../../states/modalState';
import { getOrigin } from '../../utils';
import Checkbox from './../ui/Checkbox';
import RoundButton from './../ui/RoundButton';

const CHARGE_UNIT = 10;
const MAX_CHARGABLE_COINS = 200;

interface Props {
  title: string;
  showInput?: boolean;
  description?: string;
  placeholder?: string;
  defaultValue?: number;
  confirmButtonLabel?: string;
  onClickConfirm?: (value?: any) => Promise<void> | void;
}

const ChargeModal = ({
  title,
  showInput,
  description,
  placeholder,
  defaultValue,
  confirmButtonLabel = '확인',
  onClickConfirm,
}: Props) => {
  const isMobile = useIsMobile();
  const [isBeta, setIsBeta] = useState<boolean>(false);
  const [value, setValue] = useState<number | undefined>(defaultValue);
  const [, setModalState] = useRecoilState(modalState);
  const elRef = useRef<HTMLDivElement>(null);
  const originUrl = getOrigin();

  useEffect(() => {
    const currentDate = new Date(
      new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' })
    );
    const cutoffDate = new Date('2025-01-12T23:59:59+09:00'); // 2025-01-12까지는 베타기간

    setIsBeta(currentDate <= cutoffDate);
  }, []);

  const [checkedValues, setCheckedValues] = useState({
    checkbox1: false,
    checkbox2: false,
    checkbox3: false,
  });

  const closeModal = () =>
    setModalState({
      isOpen: false,
    });

  useOutsideClick({ ref: elRef, handler: closeModal });

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(Number(e.target.value));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      onClickConfirm(value);
      closeModal();
    }
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setCheckedValues((prevValues) => {
      const newCheckedValues = {
        ...prevValues,
        [name]: checked,
      };

      return newCheckedValues;
    });
  };

  return (
    <ModalWrapper ref={elRef} style={{ maxWidth: '430px' }}>
      <ModalTitle style={{ fontSize: '22px', textAlign: 'center' }}>
        {title}
      </ModalTitle>
      {description && <ModalDesc>{description}</ModalDesc>}
      {showInput && (
        <ModalInputWrapper>
          <ModalNumberInput
            disabled
            type="number"
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
            onChange={onChange}
            value={value}
          ></ModalNumberInput>
          <ButtonWrapper
            style={{
              left: 0,
              pointerEvents: value === CHARGE_UNIT ? 'none' : 'initial',
            }}
            onClick={() => setValue((value) => value - CHARGE_UNIT)}
          >
            {value === CHARGE_UNIT ? <MinusDisabledBtn /> : <MinusPressedBtn />}
          </ButtonWrapper>
          <ButtonWrapper
            style={{
              right: 0,
              pointerEvents: value === MAX_CHARGABLE_COINS ? 'none' : 'initial',
            }}
            onClick={() => setValue((value) => value + CHARGE_UNIT)}
          >
            {value === MAX_CHARGABLE_COINS ? (
              <PlusDisabledBtn />
            ) : (
              <PlusPressedBtn />
            )}
          </ButtonWrapper>
        </ModalInputWrapper>
      )}

      <AgreementContentWrapper
        style={{
          border: checkedValues.selectAll ? '1px solid #1d90f5' : '',
          backgroundColor: checkedValues.selectAll ? '#e2f2ff' : '',
          marginTop: 6,
        }}
      >
        <AgreementContent>
          <Checkbox
            id="checkbox1"
            name="checkbox1"
            checked={checkedValues.checkbox1}
            onChange={handleCheckboxChange}
            style={{
              width: '19px',
              height: '19px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: '#e6e6e6',
            }}
          ></Checkbox>
          <AgreementLabel htmlFor="checkbox1">
            (필수) 개인정보 수집 및 이용동의
          </AgreementLabel>

          <a
            target="_blank"
            style={{
              position: 'absolute',
              right: -6,
              fontSize: 11,
              fontWeight: 600,
              padding: '4px 6px',
              borderRadius: '4px',
              textAlign: 'center',
              backgroundColor: 'transparent',
              border: '1px solid #999999',
              color: '#444444',
            }}
            href={`${originUrl}/policy/personal-info`}
          >
            내용보기
          </a>
        </AgreementContent>

        <AgreementContent>
          <Checkbox
            id="checkbox3"
            name="checkbox3"
            checked={checkedValues.checkbox3}
            onChange={handleCheckboxChange}
            style={{
              width: '19px',
              height: '19px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: '#e6e6e6',
            }}
          ></Checkbox>
          <AgreementLabel htmlFor="checkbox3">
            (필수) 결제대행서비스 이용약관 동의
          </AgreementLabel>
          <a
            target="_blank"
            style={{
              position: 'absolute',
              right: -6,
              fontSize: 11,
              fontWeight: 600,
              padding: '4px 6px',
              borderRadius: '4px',
              textAlign: 'center',
              backgroundColor: 'transparent',
              border: '1px solid #999999',
              color: '#444444',
            }}
            href={`${window.location.origin}/policy/pay-gateway`}
          >
            내용보기
          </a>
        </AgreementContent>

        <AgreementContent>
          <Checkbox
            id="checkbox2"
            name="checkbox2"
            checked={checkedValues.checkbox2}
            onChange={handleCheckboxChange}
            style={{
              width: '19px',
              height: '19px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: '#e6e6e6',
            }}
          ></Checkbox>
          <AgreementLabel htmlFor="checkbox2">
            (필수) 구매조건 확인 및 결제 진행동의
          </AgreementLabel>
        </AgreementContent>
        {isBeta && (
          <Warning>
            현재 베타테스트 기간으로
            <br />
            1/12까지 실제 결제는 진행되지 않고 무료로 사용 가능하며 <br />
            코인은 본인 계좌가 아닌 테스트 계좌에서 충전 됩니다.
            <br />
            추후 베타테스트 기간 종료 시 충전 된 모든 코인은 사라집니다.
          </Warning>
        )}

        <AgreementDetail>
          위 상품의 구매조건을 확인하였으며, 구매에 동의합니다.
        </AgreementDetail>
      </AgreementContentWrapper>
      <ModalButtonWrapper $isMobile={isMobile}>
        <ModalButton
          $isMobile={isMobile}
          style={{ backgroundColor: '#656d7d' }}
          onClick={() => closeModal()}
        >
          취소
        </ModalButton>
        <RoundButton
          style={{ width: '50%', fontSize: isMobile ? 14 : 18 }}
          onPress={() => {
            onClickConfirm(value);
            closeModal();
          }}
          isDisabled={
            (showInput && !value) ||
            Object.values(checkedValues).some((value) => !value)
          }
        >
          {confirmButtonLabel}
        </RoundButton>
      </ModalButtonWrapper>
    </ModalWrapper>
  );
};

export default ChargeModal;

const ModalWrapper = styled.div`
  max-width: 500px;
  min-height: 265px;
  background-color: #fff;
  border-radius: 20px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  flex: 1 0 0;
`;

const ModalTitle = styled.p`
  font-size: 24px;
  font-weight: bold;
  color: black;
  text-align: left;
`;

const ModalDesc = styled.p`
  font-size: 18px;
  color: #000;
  line-height: 1%.44;
  white-space: pre-wrap;
  text-align: center;
`;

const ModalInputWrapper = styled.div`
  position: relative;
`;

const ButtonWrapper = styled.div`
  border-radius: 50%;
  cursor: pointer;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`;

const ModalNumberInput = styled.input`
  width: 100%;
  height: 60px;
  border: none;
  border-bottom: 2px solid #c3cad9;
  font-size: 20px;
  color: #000;
  outline: none;
  text-align: center;
  border-bottom: 1px solid #c3cad9;
  background-color: transparent;

  &::placeholder {
    color: #c3cad9;
  }
`;

const ModalButtonWrapper = styled.div<{ $isMobile: boolean }>`
  display: flex;
  gap: 10px;
  width: 100%;
  height: ${({ $isMobile }) => ($isMobile ? '46px' : '56px')};
`;

const ModalButton = styled.button<{ $isMobile: boolean }>`
  border: none;
  width: 180px;
  height: 100%;
  outline: none;
  cursor: pointer;
  color: #fff;
  border-radius: 10px;
  font-size: ${({ $isMobile }) => ($isMobile ? '14px' : '18px')};
`;

const AgreementContentWrapper = styled.div`
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-items: center;
  flex-direction: column;
  padding: 20px;
  gap: 15px;
`;

const AgreementContent = styled.div`
  width: 100%;
  height: 100%;
  align-items: center;
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
`;

const AgreementLabel = styled.label`
  font-size: 14px;
  font-weight: 400;
  position: relative;
  bottom: 1px;
  cursor: pointer;
`;

const AgreementDetail = styled.div`
  background-color: #f2f2f2;
  width: 100%;
  font-size: 12px;
  color: #444444;
  display: flex;
  align-items: center;
  padding: 16px;
`;

const Warning = styled.p`
  color: red;
  font-size: 13px;
  word-wrap: break-word;
  white-space: pre-wrap;
  line-height: 1.5;
`;
